import { useEffect, useRef, useState } from 'react';
import tickerPollingService from '../services/tickerPollingService';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger(
  'tickerPollingHook',
  'Ticker polling hook for minute-based updates',
  false,
  'DEBUG'
);

/**
 * Hook for ticker polling functionality
 * Provides polling updates on a fixed interval
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enablePolling - Whether to enable polling (default: true)
 * @param {number} options.pollingInterval - Polling interval in milliseconds (defaults to last saved interval or 60000)
 * @param {boolean} options.autoStart - Whether to start polling automatically (default: true)
 * @param {Function} options.onUpdate - Callback for ticker updates
 * @param {Function} options.onPollingStatusChange - Callback for polling status changes
 * @returns {Object} Polling control and status
 */
export const useTickerPolling = (options = {}) => {
  const {
    enablePolling = true,
    pollingInterval: pollingIntervalOption,
    autoStart = true,
    onUpdate,
    onPollingStatusChange
  } = options;

  const hasCustomInterval = Number.isFinite(pollingIntervalOption);
  const currentServiceInterval = tickerPollingService.getCurrentInterval();
  const initialInterval = hasCustomInterval
    ? pollingIntervalOption
    : (currentServiceInterval || 60000);

  const [pollingStatus, setPollingStatus] = useState({
    isPolling: false,
    isEnabled: true,
    pollingIntervalMs: initialInterval,
    lastPollTime: null,
    lastKnownUpdateTime: null,
    consecutiveEmptyPolls: 0,
    subscriberCount: 0
  });

  const onUpdateRef = useRef(onUpdate);
  const onStatusChangeRef = useRef(onPollingStatusChange);

  // Update refs when callbacks change
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    onStatusChangeRef.current = onPollingStatusChange;
  }, [onPollingStatusChange]);

  // Keep polling enabled state in sync with the service
  useEffect(() => {
    tickerPollingService.setEnabled(enablePolling);

    if (!enablePolling) {
      setPollingStatus(prev => ({
        ...prev,
        isEnabled: false,
        isPolling: false
      }));
    }
  }, [enablePolling]);

  // Set up polling subscription
  useEffect(() => {
    if (!enablePolling) {
      return;
    }

    logger.debug('Setting up ticker polling subscription');

    const unsubscribe = tickerPollingService.subscribe((data) => {
      logger.debug('Ticker polling update received', {
        hasUpdatedTickers: Boolean(data?.updated_tickers?.length > 0),
        updateCount: data?.updated_tickers?.length || 0,
        source: data?.source || 'unknown'
      });

      if (onUpdateRef.current) {
        onUpdateRef.current(data);
      }
    });

    // Start polling if autoStart is enabled
    if (autoStart) {
      const startInterval = hasCustomInterval ? pollingIntervalOption : undefined;
      tickerPollingService.startPolling(startInterval);
    }

    // Set up status monitoring
    const statusInterval = setInterval(() => {
      const status = tickerPollingService.getStatus();
      setPollingStatus(status);

      if (onStatusChangeRef.current) {
        onStatusChangeRef.current(status);
      }
    }, 5000); // Update status every 5 seconds

    return () => {
      logger.debug('Cleaning up ticker polling subscription');
      unsubscribe();
      clearInterval(statusInterval);
    };
  }, [enablePolling, pollingIntervalOption, autoStart, hasCustomInterval]);

  // Control functions
  const startPolling = (interval) => {
    const intervalValue = Number.isFinite(interval)
      ? interval
      : (hasCustomInterval ? pollingIntervalOption : undefined);
    tickerPollingService.startPolling(intervalValue);
  };

  const stopPolling = () => {
    tickerPollingService.stopPolling();
  };

  const forcePoll = async () => {
    await tickerPollingService.forcePoll();
  };

  const setEnabled = (enabled) => {
    tickerPollingService.setEnabled(enabled);
  };

  const setPollingInterval = (intervalMs) => {
    tickerPollingService.setPollingInterval(intervalMs);
  };

  const setInitialUpdateTime = (updateTime) => {
    tickerPollingService.setInitialUpdateTime(updateTime);
  };

  return {
    // Status
    pollingStatus,

    // Control functions
    startPolling,
    stopPolling,
    forcePoll,
    setEnabled,
    setPollingInterval,
    setInitialUpdateTime,

    // Convenience properties
    isPolling: pollingStatus.isPolling,
    isEnabled: pollingStatus.isEnabled,
    lastPollTime: pollingStatus.lastPollTime,
    lastKnownUpdateTime: pollingStatus.lastKnownUpdateTime
  };
};

export default useTickerPolling;
